import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const DriverCredentialsNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [credential, setCredential] = useState(null);
  const [search, setSearch] = useState(null);
  const [credentialsData, setCredentialsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [drivers, setDrivers] = useState([]);
  const [credentialTypes, setCredentialTypes] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CredentialTypeID: "",
      DriverID: "",
      Number: "",
      Expiration: "",
    },
  });

  useEffect(() => {
    if (credential) {
      reset({
        CredentialTypeID: credential.CredentialTypeID,
        DriverID: credential.DriverID,
        Number: credential.Number,
        Expiration: credential.Expiration
          ? credential.Expiration.split("T")[0]
          : "",
      });
    }
  }, [credential, reset]);

  useEffect(() => {
    const fetchDrivers = async () => {
      const { data: Drivers, error: dError } = await supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles (
            id,
            first_name,
            last_name
          )
        `
        )
        .eq("is_active", true);

      if (!dError) {
        // Transform data to match expected format
        const transformedDrivers =
          Drivers?.map((driver) => ({
            DriverID: driver.id,
            FirstName: driver.driver_profiles?.[0]?.first_name || "",
            LastName: driver.driver_profiles?.[0]?.last_name || "",
            Email: driver.email,
          })) || [];

        setDrivers(transformedDrivers);
      }
    };
    const fetchCredentialTypes = async () => {
      const { data: Types, error: tError } = await supabase
        .from("DriverCredentialTypes")
        .select("DriverCredentialTypeID, Description");
      if (!tError) setCredentialTypes(Types || []);
    };
    fetchDrivers();
    fetchCredentialTypes();
  }, []);

  useEffect(() => {
    const fetchCredentials = async () => {
      let query = supabase
        .from("DriverCredentials")
        .select(
          "DriverCredentialID, CredentialTypeID, DriverID, Number, Expiration",
          { count: "exact" }
        )
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setCredentialsData(data);
        setTotalCount(count);
      }
    };
    fetchCredentials();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setCredential(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      CredentialTypeID: credentialTypes[0]
        ? credentialTypes[0].DriverCredentialTypeID
        : "",
      DriverID: drivers[0] ? drivers[0].DriverID : "",
      Number: "",
      Expiration: "",
    });
    setIsEdit(true);
    setCredential(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setCredential(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (credential) {
        // Update
        const { error } = await supabase
          .from("DriverCredentials")
          .update({
            CredentialTypeID: data.CredentialTypeID,
            DriverID: data.DriverID,
            Number: data.Number,
            Expiration: data.Expiration,
          })
          .eq("DriverCredentialID", credential.DriverCredentialID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase.from("DriverCredentials").insert({
          CredentialTypeID: data.CredentialTypeID,
          DriverID: data.DriverID,
          Number: data.Number,
          Expiration: data.Expiration,
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [credential, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Driver</div>
            <div className="w-35">
              <Controller
                control={control}
                name="DriverID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.DriverID?.message}
                  >
                    {drivers.map((driver) => (
                      <option key={driver.DriverID} value={driver.DriverID}>
                        {driver.FirstName} {driver.LastName}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Credential Type</div>
            <div className="w-35">
              <Controller
                control={control}
                name="CredentialTypeID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.CredentialTypeID?.message}
                  >
                    {credentialTypes.map((type) => (
                      <option
                        key={type.DriverCredentialTypeID}
                        value={type.DriverCredentialTypeID}
                      >
                        {type.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Number</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Number"
                rules={{ required: "Number is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Number?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Expiration</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Expiration"
                rules={{ required: "Expiration is required" }}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{credential ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Driver Credentials</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Driver</th>
                <th className="px-4 py-2 border">Credential Type</th>
                <th className="px-4 py-2 border">Number</th>
                <th className="px-4 py-2 border">Expiration</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {credentialsData &&
                credentialsData.map((type) => (
                  <tr
                    key={type.DriverCredentialID}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-2 border">
                      {type.DriverCredentialID}
                    </td>
                    <td className="px-4 py-2 border">
                      {drivers.find((d) => d.DriverID === type.DriverID)
                        ? `${
                            drivers.find((d) => d.DriverID === type.DriverID)
                              .FirstName
                          } ${
                            drivers.find((d) => d.DriverID === type.DriverID)
                              .LastName
                          }`
                        : ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {credentialTypes.find(
                        (c) =>
                          c.DriverCredentialTypeID === type.CredentialTypeID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">{type.Number}</td>
                    <td className="px-4 py-2 border">
                      {type.Expiration ? type.Expiration.split("T")[0] : ""}
                    </td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setCredential(type);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverCredentialsNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Number",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"Number"}>Number</option>
                <option value={"Expiration"}>Expiration</option>
              </select>
            )}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "Number", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
